/**
 * ============================================================
 * KEJJA OS — src/pages/community/CommunityFeedPage.tsx
 * Posts feed.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import {
  Megaphone, Calendar, HandHeart, MessageCircle, Plus, X,
  Pin, MapPin,
} from 'lucide-react';

import { useCommunityDetail } from './CommunityDetailLayout';
import {
  usePosts, useCreatePost, useUpdatePost, useDeletePost,
} from '@/hooks/useCommunity';
import type { CommunityPostType, CommunityPost } from '@/lib/communityApi';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const POST_TYPE_DEFS: Array<{
  value: CommunityPostType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  { value: 'announcement', label: 'Announce', icon: Megaphone, color: 'amber' },
  { value: 'event', label: 'Event', icon: Calendar, color: 'indigo' },
  { value: 'prayer_request', label: 'Prayer', icon: HandHeart, color: 'purple' },
  { value: 'general', label: 'General', icon: MessageCircle, color: 'slate' },
];

export default function CommunityFeedPage() {
  const { homeId, community, role, currentUserId } = useCommunityDetail();
  const { data, isLoading } = usePosts(homeId, community.id);
  const [showForm, setShowForm] = useState(false);

  const posts = data?.posts ?? [];
  const isAdmin = role === 'admin';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{posts.length} post{posts.length === 1 ? '' : 's'}</div>
        <Button
          size="sm"
          className="bg-rose-600 hover:bg-rose-700 text-white"
          onClick={() => setShowForm(true)}
        >
          <Plus className="w-4 h-4 mr-1" /> Post
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
      ) : posts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <MessageCircle className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No posts yet. Start the conversation.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              canDelete={isAdmin || p.postedByUserId === currentUserId}
              canPin={isAdmin}
              homeId={homeId}
              communityId={community.id}
            />
          ))}
        </div>
      )}

      {showForm && (
        <PostForm
          homeId={homeId}
          communityId={community.id}
          canPin={isAdmin}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// PostCard
// ============================================================

function PostCard({
  post, canDelete, canPin, homeId, communityId,
}: {
  post: CommunityPost;
  canDelete: boolean;
  canPin: boolean;
  homeId: string;
  communityId: string;
}) {
  const deleteMut = useDeletePost(homeId, communityId);
  const updateMut = useUpdatePost(homeId, communityId);

  const typeDef = POST_TYPE_DEFS.find((t) => t.value === post.postType) ?? POST_TYPE_DEFS[3]!;
  const Icon = typeDef.icon;

  const eventDate = post.eventAt ? new Date(post.eventAt) : null;

  return (
    <Card className={cn(post.isPinned && 'ring-1 ring-amber-500/40')}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2.5">
          <div className={cn(
            'h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
            `bg-${typeDef.color}-100 dark:bg-${typeDef.color}-950/40`,
            `text-${typeDef.color}-700 dark:text-${typeDef.color}-300`,
          )}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {post.title && <span className="font-semibold text-sm truncate">{post.title}</span>}
              <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
                {post.postType.replace('_', ' ')}
              </Badge>
              {post.isPinned && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/60 text-amber-700 dark:text-amber-300">
                  <Pin className="w-2 h-2 mr-0.5" />pinned
                </Badge>
              )}
            </div>

            {eventDate && (
              <div className="mt-1 text-[11px] text-indigo-700 dark:text-indigo-300">
                📅 {eventDate.toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short' })}
                {' · '}{eventDate.toISOString().slice(11, 16)}
                {post.eventLocation && (
                  <span className="ml-2"><MapPin className="w-2.5 h-2.5 inline" /> {post.eventLocation}</span>
                )}
              </div>
            )}

            <p className="text-xs text-foreground mt-1.5 whitespace-pre-wrap">{post.body}</p>

            <div className="flex items-center gap-3 mt-2">
              <span className="text-[10px] text-muted-foreground">
                {new Date(post.createdAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}
              </span>
              {canPin && (
                <button
                  onClick={() => updateMut.mutate({ postId: post.id, patch: { isPinned: !post.isPinned } })}
                  className="text-[10px] text-muted-foreground hover:text-amber-600"
                >
                  {post.isPinned ? 'Unpin' : 'Pin'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => {
                    if (confirm('Delete this post?')) deleteMut.mutate(post.id);
                  }}
                  className="text-[10px] text-muted-foreground hover:text-rose-600"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// PostForm
// ============================================================

function PostForm({
  homeId, communityId, canPin, onClose,
}: {
  homeId: string; communityId: string; canPin: boolean; onClose: () => void;
}) {
  const createMut = useCreatePost(homeId, communityId);
  const [postType, setPostType] = useState<CommunityPostType>('general');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('18:00');
  const [eventLocation, setEventLocation] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!body.trim()) { setErr('Say something.'); return; }

    const eventAt = postType === 'event' && eventDate
      ? new Date(`${eventDate}T${eventTime}:00Z`).toISOString()
      : null;

    createMut.mutate({
      postType,
      title: title.trim() || null,
      body: body.trim(),
      eventAt,
      eventLocation: postType === 'event' ? (eventLocation.trim() || null) : null,
      isPinned: canPin ? isPinned : false,
    }, {
      onSuccess: () => onClose(),
      onError: (e) => setErr(e.message ?? 'Could not post'),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-lg my-8">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">New post</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
            </div>

            <div className="flex gap-1.5 flex-wrap">
              {POST_TYPE_DEFS.map((t) => {
                const Icon = t.icon;
                const on = postType === t.value;
                return (
                  <button
                    key={t.value} type="button"
                    onClick={() => setPostType(t.value)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
                      on
                        ? 'border-rose-500/60 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {postType === 'announcement' && (
              <Input
                autoFocus value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (optional)"
              />
            )}

            {postType === 'event' && (
              <>
                <Input
                  autoFocus value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Event name"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Time</Label>
                    <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} className="mt-1" />
                  </div>
                </div>
                <Input
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="Location (optional)"
                />
              </>
            )}

            <textarea
              autoFocus={postType !== 'announcement' && postType !== 'event'}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What's on your mind?"
              className="w-full min-h-[100px] rounded-md border border-border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-500"
            />

            {canPin && (
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
                Pin this post to the top
              </label>
            )}

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <Button
              type="submit"
              className="bg-rose-600 hover:bg-rose-700 text-white w-full"
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Posting…' : 'Post'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
